import axios from 'axios';
import type { InjectionKey } from 'vue';
import { type ServiceResult, isServiceSuccess } from '@core/models/actionResponse';

import type { TokenRequest, TokenResponse } from './models/authModels';
import { axiosToServiceResult } from './axios';

export const AUTH_KEY: InjectionKey<IAuthenticationHandler> = Symbol();
export interface IAuthenticationHandler {
  getToken(opts?: TokenRequest): Promise<ServiceResult<TokenResponse>>;
  getClaims(): Promise<Record<string, string>>;
}

export class AuthenticationHandler implements IAuthenticationHandler {
  private currentToken: TokenResponse | null;
  private refreshPromise: Promise<ServiceResult<TokenResponse>> | null = null;

  constructor() {
    this.currentToken = null;
  }

  /**
   * Force a token refresh, bypassing the in-memory `expiration > now` short-circuit in getToken().
   * Used by the authedAxios 401 interceptor: the server can invalidate a token the client still
   * believes is valid (clock skew, server restart, session revoke), in which case getToken() would
   * keep handing back the dead token forever. Concurrent 401s share a single in-flight refresh.
   */
  async forceRefresh(): Promise<ServiceResult<TokenResponse>> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.doForceRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doForceRefresh(): Promise<ServiceResult<TokenResponse>> {
    this.currentToken = null;
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      // No session to recover — the caller is anonymous (e.g. a hosted-gallery customer).
      // Do NOT redirect to login here: public pages make authedAxios calls that can 401
      // (e.g. MImageListItem fetching /api/admin/watermarkImages/system), and redirecting
      // kicked every anonymous visitor to /account/login. Let the original 401 propagate
      // as a swallowed ServiceError, exactly like before the interceptor existed.
      this.clear();
      return { errors: ['Not authenticated.'] };
    }

    return await this.loginWithRefreshToken(refreshToken);
  }

  async getClaims(): Promise<Record<string, string>> {
    if (!this.currentToken) {
      await this.getToken();
    }

    return this.currentToken ? parseJwt(this.currentToken.token) : {};
  }

  async getToken(opts?: TokenRequest): Promise<ServiceResult<TokenResponse>> {
    const now = new Date();
    if (!opts && this.currentToken && this.currentToken.expiration > now) {
      return { data: this.currentToken };
    }

    // Share one in-flight refresh across concurrent callers (e.g. several uploads resuming after the
    // access token expires). The server is meant to revoke a refresh token once it's used, so
    // parallel refreshes with the same token would all but one fail with "Invalid token" and force
    // a re-login once that revocation works.
    if (!opts && localStorage.getItem('refreshToken')) {
      return await this.forceRefresh();
    }

    if (!opts) {
      return { errors: [`Failed to authenticate`] };
    }

    try {
      return this.processTokenResponse(await this.obtainToken(opts));
    } catch {
      return { errors: [`Failed to authenticate`] };
    }
  }

  async loginWithRefreshToken(token: string) {
    const response = await this.refreshToken(token);

    if (!isServiceSuccess(response) && response.errors && response.errors.includes('Invalid token')) {
      this.clear();
      console.log('Session expired. Redirecting to login...');
      window.location.href = `${window.location.origin}/account/login?expired=1`;
      return { errors: ['Session expired. Please log in again.'] };
    }
    return this.processTokenResponse(response);
  }

  private processTokenResponse(response: ServiceResult<TokenResponse>) {
    if (isServiceSuccess(response)) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      this.currentToken = {
        ...response.data,
        expiration: new Date(response.data.expiration),
      };
    }
    return response;
  }

  private async obtainToken(req: TokenRequest): Promise<ServiceResult<TokenResponse>> {
    return await axiosToServiceResult(axios.post<TokenResponse>('/api/auth/login', req));
  }

  private async refreshToken(refreshToken: string): Promise<ServiceResult<TokenResponse>> {
    return await axiosToServiceResult(axios.post<TokenResponse>('/api/auth/refresh', { refreshToken }));
  }

  clear() {
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authToken');
  }
}

export const authenticationHandler = new AuthenticationHandler();

export function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}
